import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  fetchQuestionsForBrand,
  deleteQuestion,
  updateQuestion,
  addQuestion,
  uploadCSV,
} from "../services/api";
import ConfirmDialog from "./ConfirmDialog";
import Toast from "./Toast";

const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

async function runWithConcurrency(items, worker, concurrency = 6) {
  const c = clamp(concurrency, 1, 20);
  let idx = 0;
  const results = [];
  const runners = Array.from({ length: c }, async () => {
    while (idx < items.length) {
      const i = idx++;
      results[i] = await worker(items[i], i);
    }
  });
  await Promise.all(runners);
  return results;
}

function KindToggle({ kind, onChange, disabled }) {
  // Mirrors the wizard's per-row badge styling so users get the same
  // clickable organic / prompted affordance everywhere they edit
  // questions.
  const isPrompted = kind === "prompted";
  return (
    <button
      type="button"
      onClick={() => onChange(isPrompted ? "organic" : "prompted")}
      disabled={disabled}
      title={
        isPrompted
          ? "Prompted: question names the brand directly. Click to flip to Organic."
          : "Organic: generic discovery question. Click to flip to Prompted."
      }
      style={{
        padding: "3px 8px",
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 900,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        cursor: disabled ? "default" : "pointer",
        border: isPrompted ? "1px solid #bfdbfe" : "1px solid #d1d5db",
        background: isPrompted ? "#eff6ff" : "#f9fafb",
        color: isPrompted ? "#1e3a8a" : "#374151",
      }}
    >
      {isPrompted ? "Prompted" : "Organic"}
    </button>
  );
}


// Parse a multi-line text blob into a clean list of questions:
// trim each line, drop empty, collapse internal whitespace, dedupe
// case-insensitively (preserving first-seen casing).
function _parseQuestionLines(blob) {
  const seen = new Set();
  const out = [];
  for (const raw of (blob || "").split(/\r?\n/)) {
    const t = raw.trim().replace(/\s+/g, " ");
    if (!t) continue;
    const k = t.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(t);
  }
  return out;
}

function AddQuestionModal({ onAdd, onClose, keepAdding, setKeepAdding, addCount }) {
  const [value, setValue] = useState("");
  const [kind, setKind] = useState("organic");
  const [saving, setSaving] = useState(false);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  // After a successful add in keepAdding mode, clear and refocus
  React.useEffect(() => {
    if (addCount > 0 && keepAdding) {
      setValue("");
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [addCount, keepAdding]);

  const lines = _parseQuestionLines(value);
  const lineCount = lines.length;

  const handleSubmit = async () => {
    if (!lineCount || saving) return;
    setSaving(true);
    try {
      // onAdd accepts an array (new bulk path) or a single string (legacy).
      // Pass the array unconditionally; parent handler branches internally.
      await onAdd(lines, kind);
    } finally {
      setSaving(false);
    }
  };

  // Enter = newline (so users can paste/type multi-line freely).
  // Ctrl/Cmd+Enter = submit.
  const handleKeyDown = (e) => {
    if (e.key === "Escape") onClose();
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onKeyDown={handleKeyDown}>
      <div className="modal-card" style={{ width: 620, maxWidth: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 18 }}>
            Add question{lineCount > 1 ? "s" : ""}
            {addCount > 0 && <span style={{ fontSize: 13, color: "#6b7280", fontWeight: 400, marginLeft: 8 }}>({addCount} added)</span>}
          </h3>
          <button className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div style={{ marginBottom: 8, color: "#374151" }}>
          Enter one question per line — paste a list to add many at once.
        </div>

        <textarea
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="fld"
          placeholder={"Type or paste questions, one per line...\ne.g.\nCare sunt cele mai bune proiecte rezidențiale din Pipera?\nHILS Nord este un proiect rezidențial de încredere?"}
          disabled={saving}
          rows={8}
          style={{ width: "100%", boxSizing: "border-box", resize: "vertical", fontFamily: "inherit", lineHeight: 1.5 }}
        />

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 12, color: "#6b7280" }}>Add as:</span>
            <KindToggle kind={kind} onChange={setKind} disabled={saving} />
          </div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            {lineCount > 0 ? `${lineCount} question${lineCount === 1 ? "" : "s"} ready` : "—"}
            <span style={{ marginLeft: 10, color: "#9ca3af" }}>Ctrl+Enter to submit</span>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#374151", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={keepAdding}
              onChange={(e) => setKeepAdding(e.target.checked)}
            />
            Keep adding
          </label>

          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn-secondary" onClick={onClose} disabled={saving}>
              {addCount > 0 ? "Done" : "Cancel"}
            </button>
            <button className="btn-primary" onClick={handleSubmit} disabled={saving || !lineCount}>
              {saving ? "Adding..." : (lineCount > 1 ? `Add ${lineCount}` : "Add")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BrandQuestions({
  brandId,
  compact = false,
  refreshSignal = 0,
  onChange,
}) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [savingIds, setSavingIds] = useState([]);
  const [deletingIds, setDeletingIds] = useState([]);

  const [toast, setToast] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmPayload, setConfirmPayload] = useState(null);

  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const notifyChange = () => {
    try {
      onChange && onChange();
    } catch (_) {}
  };

  const load = useCallback(async () => {
    if (!brandId) {
      setQuestions([]);
      setSelectedIds([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetchQuestionsForBrand(brandId);
      const list = res?.items ?? res ?? [];

      const normalized = (Array.isArray(list) ? list : []).map((it) => {
        const id = it.id ?? it.question_id ?? it.number ?? it.questionId ?? null;
        const text =
          it.text ?? it.question_text ?? it.question ?? it.questionText ?? "";
        const kind = it.kind === "prompted" ? "prompted" : "organic";
        return { id, text: String(text || ""), kind, raw: it };
      });

      setQuestions(normalized);
      setSelectedIds([]);
    } catch (err) {
      console.error("[LOAD QUESTIONS ERROR]", err);
      setQuestions([]);
      setToast({ message: "Failed to load questions", type: "error" });
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    if (brandId) load();
  }, [brandId, refreshSignal, load]);

  const total = questions.length;
  const selectedCount = selectedIds.length;

  const selectedAll = useMemo(
    () => total > 0 && selectedCount === total,
    [total, selectedCount]
  );

  const toggleSelect = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const selectAll = () => setSelectedIds(questions.map((q) => q.id));
  const deselectAll = () => setSelectedIds([]);

  const onRequestEdit = (q) => {
    setEditingQuestion(q);
    setEditOpen(true);
  };

  const handleEditConfirmed = async (value) => {
    if (!editingQuestion) return;

    const text = (value ?? "").trim().replace(/\s+/g, " ");
    if (!text) {
      setToast({ message: "Question text cannot be empty.", type: "error" });
      setEditOpen(false);
      setEditingQuestion(null);
      return;
    }

    const id = editingQuestion.id;
    if (savingIds.includes(id)) return;

    setSavingIds((s) => [...s, id]);

    try {
      await updateQuestion(brandId, id, text);
      await load();
      setToast({ message: "Question updated", type: "success" });
      notifyChange();
    } catch (err) {
      console.error(err);
      setToast({ message: "Failed to update question", type: "error" });
    } finally {
      setSavingIds((s) => s.filter((x) => x !== id));
      setEditOpen(false);
      setEditingQuestion(null);
    }
  };

  const onToggleKind = async (q) => {
    if (savingIds.includes(q.id)) return;
    const nextKind = q.kind === "prompted" ? "organic" : "prompted";
    // Optimistic flip — the badge updates immediately, the API call
    // reconciles in the background. If it fails we reload to roll back.
    setQuestions((prev) =>
      prev.map((row) => (row.id === q.id ? { ...row, kind: nextKind } : row)),
    );
    setSavingIds((s) => [...s, q.id]);
    try {
      await updateQuestion(brandId, q.id, undefined, nextKind);
      notifyChange();
    } catch (err) {
      console.error(err);
      setToast({ message: "Failed to update kind", type: "error" });
      await load();
    } finally {
      setSavingIds((s) => s.filter((x) => x !== q.id));
    }
  };

  const doDelete = async (ids) => {
    const uniq = Array.from(new Set((ids || []).filter(Boolean)));
    if (!uniq.length) return;

    setDeletingIds((prev) => [...prev, ...uniq]);

    try {
      await runWithConcurrency(uniq, async (id) => deleteQuestion(brandId, id), 6);

      await load();
      setToast({ message: `${uniq.length} question(s) deleted`, type: "success" });
      notifyChange();
    } catch (err) {
      console.error(err);
      setToast({ message: "Failed to delete questions", type: "error" });
    } finally {
      setDeletingIds((prev) => prev.filter((x) => !uniq.includes(x)));
      setSelectedIds((prev) => prev.filter((x) => !uniq.includes(x)));
    }
  };

  const onRequestDeleteSingle = (id, text) => {
    setConfirmPayload({
      title: "Delete question",
      message: `Are you sure you want to delete this question?\n\n"${text}"`,
      confirmText: "Delete",
      onConfirm: () => {
        doDelete([id]);
        closeConfirm();
      },
    });
    setConfirmOpen(true);
  };

  const onRequestDeleteSelected = () => {
    if (!selectedIds.length) return;

    setConfirmPayload({
      title: "Delete selected questions",
      message: `Are you sure you want to delete ${selectedIds.length} selected question(s)?`,
      confirmText: "Delete",
      onConfirm: () => {
        doDelete(selectedIds);
        closeConfirm();
      },
    });
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setConfirmPayload(null);
  };

  const onRequestAdd = () => setAddOpen(true);

  const onCsvButtonClick = () => {
    if (!brandId || uploading) return;
    fileInputRef.current?.click();
  };

  const onCsvFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-upload of same filename
    if (!file || !brandId) return;
    if (!/\.csv$/i.test(file.name)) {
      setToast({ message: "Please choose a .csv file", type: "error" });
      return;
    }
    setUploading(true);
    try {
      const res = await uploadCSV(file, brandId);
      const added = res?.items_count ?? (res?.items?.length ?? 0);
      const skipped = res?.skipped_rows ?? 0;
      setToast({
        message:
          `Imported ${added} question${added === 1 ? "" : "s"}` +
          (skipped ? ` (${skipped} skipped — empty or duplicate)` : ""),
        type: added > 0 ? "success" : "info",
      });
      await load();
      notifyChange();
    } catch (err) {
      console.error("[CSV UPLOAD ERROR]", err);
      setToast({
        message: `CSV upload failed: ${err?.message || "unknown error"}`,
        type: "error",
      });
    } finally {
      setUploading(false);
    }
  };

  const [keepAdding, setKeepAdding] = useState(true);
  const [addCount, setAddCount] = useState(0);

  const handleAddConfirmed = async (value, kind) => {
    // value can be either a single string (legacy callers) or an array
    // of strings (bulk modal). Normalize to an array of cleaned, deduped
    // lines so the rest of the function is uniform.
    const rawList = Array.isArray(value) ? value : [value];
    const texts = [];
    const seen = new Set();
    for (const v of rawList) {
      const t = (v ?? "").trim().replace(/\s+/g, " ");
      if (!t) continue;
      const k = t.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      texts.push(t);
    }
    if (!texts.length) {
      setToast({ message: "Question text cannot be empty.", type: "error" });
      if (!keepAdding) setAddOpen(false);
      return;
    }

    let added = 0;
    const failedTexts = [];
    for (const t of texts) {
      try {
        await addQuestion(brandId, t, kind);
        added += 1;
      } catch (err) {
        console.error("[ADD QUESTION]", t, err);
        failedTexts.push(t);
      }
    }
    await load();
    setAddCount((c) => c + added);
    if (added > 0) notifyChange();

    if (added && !failedTexts.length) {
      setToast({
        message: added === 1
          ? "Question added"
          : `${added} questions added`,
        type: "success",
      });
    } else if (added && failedTexts.length) {
      setToast({
        message: `${added} added, ${failedTexts.length} failed (likely duplicates)`,
        type: "info",
      });
    } else {
      setToast({
        message: failedTexts.length === 1
          ? "Failed to add question"
          : `Failed to add ${failedTexts.length} questions (likely duplicates)`,
        type: "error",
      });
    }

    if (!keepAdding) {
      setAddOpen(false);
      setAddCount(0);
    }
  };

  return (
    <div
      style={{
        borderRadius: 16,
        border: "1px solid var(--border)",
        background: "#fff",
        boxShadow: "0 14px 34px rgba(15,23,42,0.10)",
        padding: 14,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <div
          style={{
            fontWeight: 900,
            color: "#0f172a",
            display: "flex",
            gap: 10,
            alignItems: "baseline",
          }}
        >
          <span>Questions</span>
          <span style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>
            {brandId ? `${total} total` : "Select a brand to view questions"}
          </span>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="btn-primary" onClick={onRequestAdd} disabled={!brandId}>
            + Add
          </button>

          <button
            className="btn-secondary"
            onClick={onCsvButtonClick}
            disabled={!brandId || uploading}
            title="Upload a CSV — one question per row in a 'question_text' column. Duplicates skipped."
          >
            {uploading ? "Uploading…" : "Upload CSV"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={onCsvFileChosen}
            style={{ display: "none" }}
          />

          <button
            className="btn-secondary"
            onClick={selectAll}
            disabled={!brandId || total === 0 || selectedAll}
          >
            Select all
          </button>

          <button
            className="btn-secondary"
            onClick={deselectAll}
            disabled={!brandId || selectedCount === 0}
          >
            Deselect
          </button>

          <button
            className="btn-secondary"
            onClick={onRequestDeleteSelected}
            disabled={!brandId || !selectedCount}
            style={selectedCount ? undefined : { opacity: 0.55 }}
          >
            Delete selected {selectedCount ? `(${selectedCount})` : ""}
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ color: "#475569", fontWeight: 600, padding: 10 }}>Loading…</div>
      ) : !brandId ? (
        <div style={{ color: "#64748b", fontWeight: 300, padding: 10 }}>
          Pick a brand above. This list will update instantly after CSV uploads or edits.
        </div>
      ) : questions.length === 0 ? (
        <div
          style={{
            color: "#64748b",
            fontWeight: 600,
            padding: 14,
            borderRadius: 14,
            background: "rgba(15,23,42,0.03)",
            border: "1px dashed rgba(148,163,184,0.8)",
          }}
        >
          No questions yet. Upload a CSV or click <strong>+ Add</strong>.
        </div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {questions.map((q, idx) => {
            const isDeleting = deletingIds.includes(q.id);
            return (
              <li
                key={q.id}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                  padding: "10px 6px",
                  borderBottom: "1px solid rgba(226,232,240,0.9)",
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.includes(q.id)}
                  onChange={() => toggleSelect(q.id)}
                  style={{ marginTop: 4 }}
                />

                <div
                  style={{
                    width: 36,
                    textAlign: "right",
                    color: "#0f172a",
                    fontWeight: 800,
                  }}
                >
                  {idx + 1}.
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 10,
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          marginBottom: 6,
                          color: "#0f172a",
                          fontWeight: 500,
                          lineHeight: 1.5,
                          letterSpacing: "-0.01em",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <KindToggle
                          kind={q.kind}
                          onChange={() => onToggleKind(q)}
                          disabled={savingIds.includes(q.id)}
                        />
                        <span style={{ flex: 1, minWidth: 0 }}>{q.text}</span>
                      </div>
                      <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 300 }}>
                        internal id: {q.id}
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <button
                        className="btn-secondary"
                        title="Edit"
                        onClick={() => onRequestEdit(q)}
                        style={{ padding: "8px 10px" }}
                      >
                        ✎
                      </button>

                      <button
                        className="btn-secondary"
                        title="Delete"
                        onClick={() => onRequestDeleteSingle(q.id, q.text)}
                        disabled={isDeleting}
                        style={{
                          padding: "8px 10px",
                          opacity: isDeleting ? 0.65 : 1,
                        }}
                      >
                        {isDeleting ? "…" : "🗑"}
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title={confirmPayload?.title || "Confirm"}
        message={confirmPayload?.message || "Are you sure?"}
        confirmText={confirmPayload?.confirmText || "Yes"}
        cancelText={confirmPayload?.cancelText || "Cancel"}
        onConfirm={() => {
          try {
            confirmPayload?.onConfirm?.();
          } catch (e) {
            console.error(e);
          }
        }}
        onCancel={() => {
          setConfirmOpen(false);
          setConfirmPayload(null);
        }}
      />

      {addOpen && (
        <AddQuestionModal
          onAdd={handleAddConfirmed}
          onClose={() => { setAddOpen(false); setAddCount(0); }}
          keepAdding={keepAdding}
          setKeepAdding={setKeepAdding}
          addCount={addCount}
        />
      )}

      <ConfirmDialog
        open={editOpen}
        title="Edit question"
        message="Update question text"
        confirmText="Save"
        cancelText="Cancel"
        showInput
        defaultValue={editingQuestion?.text || ""}
        onConfirm={handleEditConfirmed}
        onCancel={() => {
          setEditOpen(false);
          setEditingQuestion(null);
        }}
      />

      <Toast message={toast?.message} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
}
